import { PostHog } from 'posthog-js';

import { SimpleEvent } from '../../hooks/contexts/resaleEvent.context';
import { ResaleData } from '../../types/resale';
import { User } from '../../types/user';
import { logger } from '../../utils/logger';
import {
  ResaleBaseProperties,
  ResaleDataLoadFailedProperties,
  ResaleEventPropertiesMap,
  ResalePageViewProperties,
  ResaleProceedToPaymentProperties,
  ResaleTicketProperties,
} from './types';

/**
 * ResaleTracking - Handles PostHog tracking for the main resale page
 *
 * Responsibilities:
 * - Track page views and user interactions
 * - Track data loading success/failures
 * - Track navigation to payment
 * - Track authentication flows
 * - Build type-safe event properties from context data
 */
export class ResaleTracking {
  private posthog: PostHog | null;
  private debug: boolean;

  constructor(posthog: PostHog | null, debug = false) {
    this.posthog = posthog;
    this.debug = debug;
  }

  /**
   * Build base properties common to all resale events
   */
  private buildBaseProperties(
    resaleData: ResaleData | null,
    event: SimpleEvent | null,
    user: User | null,
    authenticatedUser: boolean
  ): ResaleBaseProperties | null {
    // Require at minimum resale UUID and event ID
    if (!resaleData?.uuid || !event?.id) {
      logger.warn('[ResaleTracking] Missing required data for base properties', {
        hasResaleData : !!resaleData,
        hasEvent : !!event,
      });
      return null;
    }

    return {
      resale_id : resaleData.uuid,
      event_id : event.id,
      event_title : event.title,
      user_id : user?.id,
      is_authenticated : authenticatedUser,
      page_path : window.location.pathname,
      timestamp : new Date().toISOString(),
    };
  }

  /**
   * Build ticket properties from resale data
   */
  private buildTicketProperties(resaleData: ResaleData): ResaleTicketProperties {
    return {
      ticket_name : resaleData.ticket.guestType.name,
      session_name : undefined,
      price_cents : resaleData.ticket.originalPrice,
      price_formatted : `R$ ${(resaleData.ticket.originalPrice / 100).toFixed(2)}`,
      is_active : resaleData.isActive,
      is_sold : resaleData.isSold,
      is_deactivated : resaleData.isDeactivated,
    };
  }

  /**
   * Track resale page view
   * Called when user lands on the resale page
   */
  trackPageView(
    resaleData: ResaleData | null,
    event: SimpleEvent | null,
    user: User | null,
    authenticatedUser: boolean
  ): void {
    const baseProperties = this.buildBaseProperties(
      resaleData,
      event,
      user,
      authenticatedUser
    );

    if (!baseProperties) {
      logger.error('[ResaleTracking] Cannot track page view - missing required data');
      return;
    }

    const properties: ResalePageViewProperties = {
      ...baseProperties,
      ...(resaleData ? this.buildTicketProperties(resaleData) : {}),
    };

    this.trackEvent('resale_page_view', properties);
  }

  /**
   * Track proceed to payment button click
   * Called when user clicks "Proceed to Payment" button
   */
  trackProceedToPaymentClicked(
    resaleData: ResaleData,
    event: SimpleEvent,
    user: User | null,
    authenticatedUser: boolean,
    elementId?: string
  ): void {
    const baseProperties = this.buildBaseProperties(
      resaleData,
      event,
      user,
      authenticatedUser
    );

    if (!baseProperties) {
      logger.error(
        '[ResaleTracking] Cannot track proceed to payment - missing required data'
      );
      return;
    }

    const properties: ResaleProceedToPaymentProperties = {
      ...baseProperties,
      ...this.buildTicketProperties(resaleData),
      interaction_type : 'click',
      element_id : elementId,
      required_auth : !authenticatedUser,
    };

    this.trackEvent('resale_proceed_to_payment_clicked', properties);
  }

  /**
   * Track data load failure
   * Called when resale or event data fails to load
   */
  trackDataLoadFailed(
    resaleId: string,
    errorMessage: string,
    errorType: 'resale_not_found' | 'event_not_found' | 'api_error' = 'api_error',
    errorContext?: string
  ): void {
    const properties: ResaleDataLoadFailedProperties = {
      resale_id : resaleId,
      event_id : 0, // Unknown at this point
      is_authenticated : false, // Unknown at this point
      page_path : window.location.pathname,
      timestamp : new Date().toISOString(),
      error_message : errorMessage,
      error_type : errorType,
      error_context : errorContext,
    };

    this.trackEvent('resale_data_load_failed', properties);
  }

  /**
   * Track successful data load
   * Called when both resale and event data load successfully
   */
  trackDataLoadSuccess(
    resaleData: ResaleData,
    event: SimpleEvent,
    user: User | null,
    authenticatedUser: boolean
  ): void {
    const baseProperties = this.buildBaseProperties(
      resaleData,
      event,
      user,
      authenticatedUser
    );

    if (!baseProperties) {
      logger.error(
        '[ResaleTracking] Cannot track data load success - missing required data'
      );
      return;
    }

    const properties: ResaleEventPropertiesMap['resale_data_load_success'] = {
      ...baseProperties,
      ...this.buildTicketProperties(resaleData),
    };

    this.trackEvent('resale_data_load_success', properties);
  }

  /**
   * Track login required
   * Called when user tries to proceed without authentication
   */
  trackLoginRequired(
    resaleData: ResaleData,
    event: SimpleEvent,
    triggerElement?: string
  ): void {
    const baseProperties = this.buildBaseProperties(resaleData, event, null, false);

    if (!baseProperties) {
      logger.error('[ResaleTracking] Cannot track login required - missing required data');
      return;
    }

    const properties: ResaleEventPropertiesMap['resale_login_required'] = {
      ...baseProperties,
      interaction_type : 'click',
      element_id : triggerElement,
      required_auth : true,
    };

    this.trackEvent('resale_login_required', properties);
  }

  /**
   * Track login completed
   * Called when user successfully authenticates
   */
  trackLoginCompleted(
    resaleData: ResaleData,
    event: SimpleEvent,
    user: User
  ): void {
    const baseProperties = this.buildBaseProperties(resaleData, event, user, true);

    if (!baseProperties) {
      logger.error('[ResaleTracking] Cannot track login completed - missing required data');
      return;
    }

    const properties: ResaleEventPropertiesMap['resale_login_completed'] = {
      ...baseProperties,
      interaction_type : 'navigation',
      required_auth : true,
    };

    this.trackEvent('resale_login_completed', properties);
  }

  /**
   * Track share event clicked
   * Called when user clicks "Share Event" link
   */
  trackShareClicked(
    resaleData: ResaleData,
    event: SimpleEvent,
    user: User | null,
    authenticatedUser: boolean
  ): void {
    const baseProperties = this.buildBaseProperties(
      resaleData,
      event,
      user,
      authenticatedUser
    );

    if (!baseProperties) {
      logger.error('[ResaleTracking] Cannot track share clicked - missing required data');
      return;
    }

    const properties: ResaleEventPropertiesMap['resale_share_clicked'] = {
      ...baseProperties,
      interaction_type : 'click',
      element_id : 'share-event-link',
    };

    this.trackEvent('resale_share_clicked', properties);
  }

  /**
   * Track event page clicked
   * Called when user clicks "See Event Page" link
   */
  trackEventPageClicked(
    resaleData: ResaleData,
    event: SimpleEvent,
    user: User | null,
    authenticatedUser: boolean
  ): void {
    const baseProperties = this.buildBaseProperties(
      resaleData,
      event,
      user,
      authenticatedUser
    );

    if (!baseProperties) {
      logger.error('[ResaleTracking] Cannot track event page clicked - missing required data');
      return;
    }

    const properties: ResaleEventPropertiesMap['resale_event_page_clicked'] = {
      ...baseProperties,
      interaction_type : 'click',
      element_id : 'event-page-link',
    };

    this.trackEvent('resale_event_page_clicked', properties);
  }

  /**
   * Generic track event method
   * Handles the actual PostHog capture with error handling
   */
  private trackEvent<T extends keyof ResaleEventPropertiesMap>(
    eventName: T,
    properties: ResaleEventPropertiesMap[T]
  ): void {
    try {
      if (!this.posthog) {
        if (this.debug) {
          logger.warn('[ResaleTracking] PostHog not initialized, event not tracked:', {
            eventName,
            properties,
          });
        }
        return;
      }

      this.posthog.capture(eventName, properties);

      if (this.debug) {
        logger.info('[ResaleTracking] Event tracked:', {
          eventName,
          properties,
        });
      }
    } catch (error) {
      logger.error('[ResaleTracking] Failed to track event:', {
        eventName,
        error,
      });
    }
  }
}
